import { z } from "zod";
import { base64ToBytes, bytesToBase64 } from "./base64.js";

/**
 * The content codec (SPEC §3, decision 2).
 *
 * Every user-authored value travels inside a ContentBlob. The server stores and
 * forwards blobs but never looks inside one — in `e2ee` mode it cannot, and in
 * `none` mode it must not, so that switching a space between modes is a config
 * change rather than a migration.
 */

export const ENCRYPTION_MODES = ["none", "e2ee"] as const;
export type EncryptionMode = (typeof ENCRYPTION_MODES)[number];

export const encryptionModeSchema = z.enum(ENCRYPTION_MODES);

export const CONTENT_BLOB_VERSION = 1;

export const contentBlobSchema = z.discriminatedUnion("mode", [
  z.object({
    v: z.literal(CONTENT_BLOB_VERSION),
    mode: z.literal("none"),
    data: z.unknown(),
  }),
  z.object({
    v: z.literal(CONTENT_BLOB_VERSION),
    mode: z.literal("e2ee"),
    iv: z.string().min(1),
    ct: z.string().min(1),
  }),
]);

export type ContentBlob = z.infer<typeof contentBlobSchema>;

export class CodecError extends Error {
  override name = "CodecError";
}

export interface Codec {
  readonly mode: EncryptionMode;
  encode(value: unknown): Promise<ContentBlob>;
  /**
   * Decoding always validates against a schema. A blob that decrypts to
   * unexpected shape is a bug or tampering, and either way must not reach the UI
   * as a partially-typed object.
   */
  decode<T>(blob: ContentBlob, schema: z.ZodType<T>): Promise<T>;
}

/** `none` mode: the blob carries plain JSON. Encode/decode are near-identity. */
export function createPlainCodec(): Codec {
  return {
    mode: "none",

    async encode(value) {
      return { v: CONTENT_BLOB_VERSION, mode: "none", data: value ?? null };
    },

    async decode(blob, schema) {
      if (blob.mode !== "none") {
        throw new CodecError(
          `plain codec received a ${blob.mode} blob; the space key is missing`,
        );
      }
      const parsed = schema.safeParse(blob.data);
      if (!parsed.success) {
        throw new CodecError(`decoded content failed validation: ${parsed.error.message}`);
      }
      return parsed.data;
    },
  };
}

const IV_BYTES = 12; // AES-GCM standard nonce length

/**
 * `e2ee` mode: AES-256-GCM under the space key.
 *
 * The key is a non-extractable CryptoKey so that neither page scripts nor the
 * service worker can read the raw bytes back out of IndexedDB (SPEC §3).
 */
export function createAesGcmCodec(key: CryptoKey): Codec {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  return {
    mode: "e2ee",

    async encode(value) {
      const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
      const plaintext = encoder.encode(JSON.stringify(value ?? null));

      const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        plaintext,
      );

      return {
        v: CONTENT_BLOB_VERSION,
        mode: "e2ee",
        iv: bytesToBase64(iv),
        ct: bytesToBase64(new Uint8Array(ciphertext)),
      };
    },

    async decode(blob, schema) {
      if (blob.mode !== "e2ee") {
        throw new CodecError(
          "e2ee codec received a plaintext blob; refusing to trust it",
        );
      }

      let plaintext: ArrayBuffer;
      try {
        plaintext = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv: base64ToBytes(blob.iv) },
          key,
          base64ToBytes(blob.ct),
        );
      } catch {
        // GCM authentication failure — wrong key or tampered ciphertext.
        throw new CodecError("could not decrypt content with this space key");
      }

      let value: unknown;
      try {
        value = JSON.parse(decoder.decode(plaintext));
      } catch {
        throw new CodecError("decrypted content was not valid JSON");
      }

      const parsed = schema.safeParse(value);
      if (!parsed.success) {
        throw new CodecError(`decoded content failed validation: ${parsed.error.message}`);
      }
      return parsed.data;
    },
  };
}

export function createCodec(
  mode: EncryptionMode,
  key?: CryptoKey | undefined,
): Codec {
  if (mode === "none") return createPlainCodec();
  if (!key) throw new CodecError("e2ee space requires a key to build a codec");
  return createAesGcmCodec(key);
}
